import React, { useCallback, useRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import sortBy from 'lodash/sortBy';
import { Button } from '@els/els-react--button';
import { Checkbox } from '@els/els-react--checkbox';
// import { TextInput } from '@els/els-react--text-input'; // restore with the facet search field (see FacetContent)
import { RawAutosuggest as Autosuggest } from '@els/els-react--autosuggest';
import { Pill } from '@els/els-react--pill';
import { TabGroup, TabPanel } from '@els/els-react--tab-group';

import {
  SUPPLIERS_TAB_ID,
  SUPPLIER_ALPHABET_FILTERS,
  SHIPPING_TIME_TAB_ID,
  TAB_CONFIGS,
  INITIAL_SELECTIONS,
  filterOptionsBySearch,
  getSafeDomId,
  getSupplierAlphabetFilter,
  getTabButtonId,
  getTabConfig,
  getTabLabel,
  normalizeText,
  splitIntoColumns,
} from './CommercialSubstancesPreferencesConstants';

import './CommercialSubstancesPreferencesEditor.scss';

const RESPONSIVE_TABS_MEDIA_QUERY = '(max-width: 64rem)';

function getIsResponsiveTabsLayout() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return Boolean(window.matchMedia(RESPONSIVE_TABS_MEDIA_QUERY)?.matches);
}

function useResponsiveTabsLayout() {
  const [isResponsiveTabsLayout, setIsResponsiveTabsLayout] = useState(getIsResponsiveTabsLayout);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(RESPONSIVE_TABS_MEDIA_QUERY);

    if (!mediaQuery) {
      return undefined;
    }

    const handleChange = (event) => {
      setIsResponsiveTabsLayout(event.matches);
    };

    setIsResponsiveTabsLayout(Boolean(mediaQuery.matches));

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }

    return undefined;
  }, []);

  return isResponsiveTabsLayout;
}

function SuppliersContent({
  idPrefix,
  supplierOptions,
  selections,
  supplierOptionsByName,
  searchValue = '',
  onSearchChange,
  onToggleSelection,
  onClearSelection,
}) {
  const suppliersContentRef = useRef(null);
  const selectedPillsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const suppliersTabConfig = getTabConfig(SUPPLIERS_TAB_ID);
  const [activeSupplierFilter, setActiveSupplierFilter] = useState(SUPPLIER_ALPHABET_FILTERS[0]);
  const filteredSupplierOptions = useMemo(
    () => sortBy(supplierOptions, ['label']).filter((option) => getSupplierAlphabetFilter(option) === activeSupplierFilter),
    [supplierOptions, activeSupplierFilter]
  );
  const supplierSuggestions = useMemo(
    () => supplierOptions.map((option) => ({ inputValue: option.label, displayValue: option.label, supplierName: option.name })),
    [supplierOptions]
  );
  const filteredSuggestions = useMemo(() => {
    if (!searchValue) return supplierSuggestions;
    const normalizedSearch = normalizeText(searchValue);
    return supplierSuggestions.filter((s) => normalizeText(s.displayValue).includes(normalizedSearch));
  }, [searchValue, supplierSuggestions]);
  const selectedSupplierItems = selections.map((supplierName) => ({
    supplierName,
    label: supplierOptionsByName[supplierName]?.label || supplierName,
  }));

  useLayoutEffect(() => {
    const pendingFocus = pendingFocusRef.current;

    if (!pendingFocus || selections.includes(pendingFocus.removedSupplierName)) {
      return;
    }

    const fallbackIndex = pendingFocus.removedIndex > 0 ? pendingFocus.removedIndex - 1 : 0;
    const closeButton = selectedPillsRef.current
      ?.querySelectorAll('[role="listitem"]')
      [fallbackIndex]?.querySelector('.c-els-pill__close-button');

    pendingFocusRef.current = null;
    if (closeButton) {
      closeButton.focus();
      return;
    }

    const suppliersSearch = suppliersContentRef.current?.querySelector('input[id$="-suppliers-search"]');
    if (suppliersSearch) {
      suppliersSearch.focus();
      return;
    }

    suppliersContentRef.current?.querySelector('.commercial-substances-preferences-editor__options-list input[type="checkbox"]')?.focus();
  }, [selections]);

  const removeSelectedSupplier = (supplierName) => {
    const removedIndex = selections.indexOf(supplierName);

    pendingFocusRef.current = {
      removedIndex,
      removedSupplierName: supplierName,
    };
    onToggleSelection(SUPPLIERS_TAB_ID, supplierName, false);
  };

  return (
    <div ref={suppliersContentRef} className="commercial-substances-preferences-editor__suppliers-layout">
      <div className="commercial-substances-preferences-editor__suppliers-column">
        <div className="u-els-margin-bottom-1o2">
          <Autosuggest
            id={`${idPrefix}-suppliers-autosuggest`}
            data-e2e={`${idPrefix}-suppliers-search`}
            className="commercial-substances-preferences-editor__search-input"
            filterSuggestions={false}
            maxSuggestions={filteredSuggestions.length}
            shouldAlwaysChooseSuggestion={false}
            suggestions={filteredSuggestions}
            textInputProps={{
              id: `${idPrefix}-suppliers-search`,
              name: `${idPrefix}-suppliers-search`,
              label: 'Search by supplier name',
              size: 'small',
              placeholder: suppliersTabConfig.searchPlaceholder,
              iconRight: {
                id: `${idPrefix}-suppliers-search-icon`,
                sprite: 'Search',
                children: 'Search',
                isDecorative: true,
              },
            }}
            onChange={(value) => onSearchChange(SUPPLIERS_TAB_ID, value)}
            onSelectSuggestion={(value, selectedSuggestion) => {
              const supplierName = selectedSuggestion?.supplierName;

              if (supplierName && !selections.includes(supplierName)) {
                onToggleSelection(SUPPLIERS_TAB_ID, supplierName, true);
              }

              onSearchChange(SUPPLIERS_TAB_ID, value || '');
            }}
          />
        </div>
        <div
          className="commercial-substances-preferences-editor__supplier-alpha-pagination u-els-margin-bottom-1o2"
          aria-label="Filter suppliers alphabetically">
          {SUPPLIER_ALPHABET_FILTERS.map((supplierFilter) => (
            <Button
              key={supplierFilter}
              type="link"
              data-e2e={`${idPrefix}-supplier-alpha-${getSafeDomId(supplierFilter)}`}
              className={classnames('commercial-substances-preferences-editor__supplier-alpha-button', {
                'commercial-substances-preferences-editor__supplier-alpha-button--active': activeSupplierFilter === supplierFilter,
              })}
              onClick={() => {
                setActiveSupplierFilter(supplierFilter);
              }}>
              {supplierFilter}
            </Button>
          ))}
        </div>
        <div className="commercial-substances-preferences-editor__options-list" data-e2e={`${idPrefix}-suppliers-options`}>
          {filteredSupplierOptions.map((option) => (
            <Checkbox
              key={option.name}
              id={`${idPrefix}-supplier-${getSafeDomId(option.name)}`}
              data-e2e={`${idPrefix}-supplier-${option.name}`}
              label={option.label}
              checked={selections.includes(option.name)}
              onChange={(event) => {
                const checked = event?.target?.checked ?? false;
                onToggleSelection(SUPPLIERS_TAB_ID, option.name, checked);
              }}
            />
          ))}
        </div>
      </div>

      <div className="commercial-substances-preferences-editor__selected-panel">
        <div className="commercial-substances-preferences-editor__selected-header">
          <Pill>{`${selections.length || 0}`}</Pill>
          <span>Chosen suppliers</span>
          {selections.length ? (
            <Button type="link" data-e2e={`${idPrefix}-suppliers-clear-selected`} onClick={() => onClearSelection(SUPPLIERS_TAB_ID)}>
              Clear selected
            </Button>
          ) : null}
        </div>

        <div
          ref={selectedPillsRef}
          className="commercial-substances-preferences-editor__selected-pills u-els-padding-left-1o2"
          data-e2e={`${idPrefix}-suppliers-selected-pills`}
          role="list"
          aria-label="List of Chosen suppliers">
          {selectedSupplierItems.map((selectedSupplier) => (
            <div key={selectedSupplier.supplierName} role="listitem">
              <Pill onClose={() => removeSelectedSupplier(selectedSupplier.supplierName)}>{selectedSupplier.label}</Pill>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

SuppliersContent.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  supplierOptions: PropTypes.array.isRequired,
  selections: PropTypes.array.isRequired,
  supplierOptionsByName: PropTypes.object.isRequired,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  onToggleSelection: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
};

function FacetContent({ idPrefix, activeTab, selections, searchValue, onToggleSelection, onClearSelection, options }) {
  const filteredOptions = activeTab === SHIPPING_TIME_TAB_ID ? options : filterOptionsBySearch(options, searchValue);
  const optionColumns = useMemo(() => {
    const [leftColumnOptions, rightColumnOptions] = splitIntoColumns(filteredOptions);
    return [
      { key: 'left', items: leftColumnOptions },
      { key: 'right', items: rightColumnOptions },
    ];
  }, [filteredOptions]);
  // const activeTabConfig = getTabConfig(activeTab); // restore with the facet search field below

  return (
    <div className="commercial-substances-preferences-editor__facet-layout">
      <div className="commercial-substances-preferences-editor__selected-header">
        <Pill>{`${selections.length || 0}`}</Pill>
        <span>Selected</span>
        {selections.length ? (
          <Button type="link" data-e2e={`${idPrefix}-${activeTab}-clear-selected`} onClick={() => onClearSelection(activeTab)}>
            Clear selected
          </Button>
        ) : null}
      </div>

      <div className="commercial-substances-preferences-editor__facet-options-grid">
        {optionColumns.map((column) => (
          <div key={column.key} className="commercial-substances-preferences-editor__facet-options-column">
            {(column.items || []).map((option, optionIndex) => (
              <Checkbox
                key={option.id}
                id={`${idPrefix}-${getSafeDomId(activeTab)}-${column.key}-${optionIndex}-${getSafeDomId(option.id)}`}
                data-e2e={`${idPrefix}-${activeTab}-${option.id}`}
                label={option.label}
                checked={selections.includes(option.id)}
                onChange={(event) => {
                  const checked = event?.target?.checked ?? false;
                  onToggleSelection(activeTab, option.id, checked);
                }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

FacetContent.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  activeTab: PropTypes.string.isRequired,
  selections: PropTypes.array.isRequired,
  searchValue: PropTypes.string.isRequired,
  // onSearchChange: PropTypes.func.isRequired, // restore with the facet search field
  onToggleSelection: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
};

function CommercialSubstancesPreferencesEditor({
  idPrefix = 'commercial-substances-preferences',
  panelId = 'commercial-substances-preferences-tab-panel',
  tabsLabel = 'Commercial substances preferences sections',
  activeTab = SUPPLIERS_TAB_ID,
  tabs = TAB_CONFIGS,
  selections = INITIAL_SELECTIONS,
  searchValues = {},
  supplierOptions = [],
  supplierOptionsByName = {},
  optionsByTab = {},
  onTabChange = () => {},
  onSearchChange = () => {},
  onToggleSelection = () => {},
  onClearSelection = () => {},
  onCancel = () => {},
  onSave = () => {},
  isSaving = false,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  showTabs = true,
  showFooter = true,
  showContentHeader = true,
  className,
}) {
  const activeSelections = selections[activeTab] || [];
  const activeSearchValue = searchValues[activeTab] || '';
  const activeOptions = optionsByTab[activeTab] || [];

  const supplierOptionsLookup = useMemo(
    () =>
      supplierOptions.reduce((accumulator, option) => {
        accumulator[option.name] = option;
        return accumulator;
      }, {}),
    [supplierOptions]
  );
  const normalizedSupplierOptionsByName = Object.keys(supplierOptionsByName).length ? supplierOptionsByName : supplierOptionsLookup;

  const tabGroupRef = useRef();
  const isResponsiveTabsLayout = useResponsiveTabsLayout();

  const renderTabContent = (tabId, tabSelections, tabSearchValue, tabOptions, tabPanelId = panelId, withPanelSemantics = false) => {
    const tabConfig = getTabConfig(tabId);

    return (
      <div
        id={tabPanelId}
        className="commercial-substances-preferences-editor__content"
        role={withPanelSemantics ? 'tabpanel' : undefined}
        aria-labelledby={withPanelSemantics ? getTabButtonId(tabId, idPrefix) : undefined}>
        {showContentHeader ? <h3 className="commercial-substances-preferences-editor__section-heading">{tabConfig.heading}</h3> : null}
        {tabId === SUPPLIERS_TAB_ID ? (
          <SuppliersContent
            idPrefix={idPrefix}
            supplierOptions={supplierOptions}
            supplierOptionsByName={normalizedSupplierOptionsByName}
            selections={tabSelections}
            searchValue={tabSearchValue}
            onSearchChange={onSearchChange}
            onToggleSelection={onToggleSelection}
            onClearSelection={onClearSelection}
          />
        ) : (
          <FacetContent
            idPrefix={idPrefix}
            activeTab={tabId}
            selections={tabSelections}
            searchValue={tabSearchValue}
            options={tabOptions}
            onToggleSelection={onToggleSelection}
            onClearSelection={onClearSelection}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!showTabs || !isResponsiveTabsLayout || !tabGroupRef.current) return;
    const activeTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
    if (activeTabIndex !== -1) {
      tabGroupRef.current.setActiveTab(activeTabIndex);
    }
  }, [activeTab, isResponsiveTabsLayout, tabs, showTabs]);

  const tabGroupTabs = useMemo(
    () =>
      tabs.map((tab) => (
        <span key={tab.id} data-e2e={`${idPrefix}-tab-${tab.id}`}>
          {getTabLabel(tab, selections)}
        </span>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabs, selections]
  );
  const handleTabChange = useCallback(
    (tabIndex) => {
      const tab = tabs[Number(tabIndex)];
      if (tab) onTabChange(tab.id);
    },
    [tabs, onTabChange]
  );

  let editorContent = renderTabContent(activeTab, activeSelections, activeSearchValue, activeOptions);

  if (showTabs && isResponsiveTabsLayout) {
    editorContent = (
      <div className="commercial-substances-preferences-editor__responsive-tabs">
        <TabGroup
          ref={tabGroupRef}
          id={`${idPrefix}-tabs`}
          isControlled
          label={tabsLabel}
          hasDivider
          noDropdown
          tabs={tabGroupTabs}
          onTabChange={handleTabChange}>
          {tabs.map((tab) => {
            const tabSelections = selections[tab.id] || [];
            const tabSearchValue = searchValues[tab.id] || '';
            const tabOptions = optionsByTab[tab.id] || [];
            return (
              <TabPanel key={tab.id}>
                {renderTabContent(tab.id, tabSelections, tabSearchValue, tabOptions, `${panelId}-${tab.id}`)}
              </TabPanel>
            );
          })}
        </TabGroup>
      </div>
    );
  } else if (showTabs) {
    editorContent = (
      <div className="commercial-substances-preferences-editor__layout">
        <nav className="commercial-substances-preferences-editor__side-nav" aria-label={tabsLabel}>
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;

            return (
              <Button
                key={tab.id}
                id={getTabButtonId(tab.id, idPrefix)}
                type="borderless"
                data-e2e={`${idPrefix}-side-nav-${tab.id}`}
                data-testid={`${idPrefix}-side-nav-${tab.id}`}
                className={classnames('commercial-substances-preferences-editor__side-nav-button', {
                  'commercial-substances-preferences-editor__side-nav-button--active': isActive,
                })}
                aria-current={isActive ? 'page' : undefined}
                aria-controls={panelId}
                onClick={() => onTabChange(tab.id)}>
                {getTabLabel(tab, selections)}
              </Button>
            );
          })}
        </nav>
        {renderTabContent(activeTab, activeSelections, activeSearchValue, activeOptions, panelId, true)}
      </div>
    );
  }

  return (
    <div className={classnames('commercial-substances-preferences-editor', className)}>
      <div
        className={classnames('commercial-substances-preferences-editor__body', {
          'commercial-substances-preferences-editor__body--single-panel': !showTabs,
        })}>
        {editorContent}
      </div>

      {showFooter ? (
        <div className="commercial-substances-preferences-editor__footer">
          <Button type="borderless" data-e2e={`${idPrefix}-cancel`} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button type="primary" data-e2e={`${idPrefix}-save`} disabled={isSaving} onClick={onSave}>
            {saveLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

CommercialSubstancesPreferencesEditor.propTypes = {
  idPrefix: PropTypes.string,
  panelId: PropTypes.string,
  tabsLabel: PropTypes.string,
  activeTab: PropTypes.string,
  tabs: PropTypes.array,
  selections: PropTypes.object,
  searchValues: PropTypes.object,
  supplierOptions: PropTypes.array,
  supplierOptionsByName: PropTypes.object,
  optionsByTab: PropTypes.object,
  onTabChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  onToggleSelection: PropTypes.func,
  onClearSelection: PropTypes.func,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  isSaving: PropTypes.bool,
  saveLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  showTabs: PropTypes.bool,
  showFooter: PropTypes.bool,
  showContentHeader: PropTypes.bool,
  className: PropTypes.string,
};

export default CommercialSubstancesPreferencesEditor;
